import { pushToDataLayer } from "./analytics.js";

const MEASUREMENT_ID = "G-YGE4Z80K66";
const FIELD_SELECTOR = 'input[name="applicant[client_id]"]';

// Set when gtag answers with no client id, so a submission can distinguish
// "Analytics said it has none" from "Analytics never answered at all".
let gtagRepliedNull = false;

// One failure event per page, however many times the form is submitted.
let failureReported = false;

// GA stores the client id verbatim in the `_ga` cookie as
// `GA1.<depth>.<id>.<ts>`; the client id is the trailing `<id>.<ts>`.
export function clientIdFromCookie() {
  const raw = document.cookie.match(/(?:^|;\s*)_ga=([^;]*)/)?.[1];
  if (!raw) return null;

  // A malformed percent escape makes decodeURIComponent throw. Left uncaught
  // that surfaces inside the submit handler, after preventDefault() and with
  // the button already disabled, which strands the form.
  let value;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return null;
  }

  // A short or malformed value must yield null rather than a plausible-looking
  // wrong id.
  const parts = value.split(".");
  return parts.length >= 4 ? parts.slice(-2).join(".") : null;
}

function writeToFields(clientId) {
  document.querySelectorAll(FIELD_SELECTOR).forEach((field) => {
    field.value = clientId;
  });
}

// Run once on page load. Both halves are free - reading a cookie is
// synchronous, and `get` resolves from gtag's own state rather than fetching
// anything - so this costs nothing at load. Asking here instead of at submit
// gives the answer the whole time the visitor spends filling the form to
// arrive, rather than holding a disabled button while it does.
export function captureClientId() {
  if (!document.querySelector(FIELD_SELECTOR)) return;

  const fromCookie = clientIdFromCookie();
  if (fromCookie) writeToFields(fromCookie);

  if (typeof window.gtag !== "function") {
    window.gtag = function () {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(arguments);
    };
  }

  // Deliberately no deadline. Nothing waits on this, so a late answer is still
  // worth having, and gtag is the documented source - it overwrites the
  // cookie-derived value rather than the other way round.
  window.gtag("get", MEASUREMENT_ID, "client_id", (clientId) => {
    if (clientId) {
      writeToFields(clientId);
    } else {
      gtagRepliedNull = true;
    }
  });
}

// Last chance at submit: the cookie can appear after page load. Entirely
// synchronous - a submission never waits on the client id.
export function ensureClientId(form) {
  const field = form.querySelector(FIELD_SELECTOR);
  if (!field || field.value) return;

  const fromCookie = clientIdFromCookie();
  if (fromCookie) {
    field.value = fromCookie;
    return;
  }

  // Filling the field is worth retrying on every submit, since the cookie can
  // appear at any time, but reporting is not: a visitor who corrects a phone
  // number and resubmits is one lead, and counting them as several failures
  // inflates the very rate this is measured by.
  if (failureReported) return;
  failureReported = true;

  // Reason tokens, not sentences: this string becomes the GA4 `error_message`
  // parameter value and is grouped on in reports. `no_gtag_response` covers
  // both Analytics being blocked and Analytics never having finished loading -
  // from here the two are indistinguishable.
  const reason = gtagRepliedNull ? "null_client_id" : "no_gtag_response";
  console.error("client_id_fetch_failure", reason);
  pushToDataLayer("client_id_fetch_failure", { error_message: reason });
}
